import { DONATION_RECURRENCES, DonationFrequency } from "@/common/helpers/donationRecurrence";
import { DafPaymentResume } from "../blocks/payment/DafPaymentFlow/DafPaymentResume/DafPaymentResume";
import React, { useState, useCallback, useMemo, useContext, useEffect } from "react";

import { FormType, getCurrencyFromCountry } from "@simplyk/common";
import { Control, FormProvider, useForm, useWatch } from "react-hook-form";
import { v4 } from "uuid";

import { buildCommunicationPreferenceDefaults } from "../helpers/communicationPreferenceDefaults";
import { getBannerAspectRatio } from "../helpers/getBannerAspectRatio";
import { getDefaultRecurrenceInterval } from "../helpers/getDefaultRecurrenceInterval";
import { useLocaleField } from "../helpers/useLocaleField";
import { applyContactPrefill } from "../helpers/applyContactPrefill";
import { usePrefillSearchParams } from "../helpers/usePrefillSearchParams";

import { DonationPaymentInput, DonationFormV2Context, FormV2Context } from "./FormV2Context";
import { useGetSuggestedAmountsAbTest } from "./helpers/useGetSuggestedAmountsAbTest";

import { FormStepKey } from "@/canvas/features/FormV2/steps/FormStepKey";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useDonationFormUpdated } from "@/common/hooks/useDonationFormUpdated";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { OrganizationOutput, QuestionOutput, DonationFormFrontendPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getStripeSubscriptionRecurrence } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";

export interface DonationFormV2ProviderProps {
  type: FormType.DonationForm;
  formObject: DonationFormFrontendPageOutput;
  currentStep: FormStepKey;
  setCurrentStep: (step: FormStepKey) => void;
  children: React.ReactNode;
  isEmbed: boolean;
}

export const DonationFormV2Provider: React.FC<DonationFormV2ProviderProps> = ({
  formObject,
  currentStep,
  setCurrentStep,
  children,
  isEmbed,
}) => {
  const { contactInfo: prefillSearchParams, isPrefillPending } = usePrefillSearchParams();
  const { isPreview } = usePreviewContext();

  const [validationMode, setValidationMode] = useState<"onBlur" | "onChange">("onBlur");

  const bannerAspectRatio = useMemo(
    () => getBannerAspectRatio(formObject.isSquareBanner, formObject.bannerVideoUrl),
    [formObject.isSquareBanner, formObject.bannerVideoUrl]
  );

  const getLocaleField = useLocaleField();
  const formFields = formObject.donationFormFields || [];
  const localeField = getLocaleField(formFields)!;
  const donationFormField = localeField;

  // Suggested Amount ABCDEF test - Use pre-computed transforms from SSR
  const {
    suggestedAmountsExperimentData,
    suggestedAmounts,
    lastInteractionWasClickedAmount,
    lastClickedAmountWasIndex,
    handleSuggestedAmountClicked,
    handleManualAmountInputChanged,
  } = useGetSuggestedAmountsAbTest(formObject.suggestedAmountsExperiment, donationFormField, isPreview);

  const hasSuggestedAmountsPerType = Object.fromEntries(
    DONATION_RECURRENCES.map(({ frequency }) => [
      frequency,
      suggestedAmounts.some((amount) => amount.recurrenceInterval === frequency),
    ])
  ) as Record<DonationFrequency, boolean>;

  const frequenciesWithAmounts = DONATION_RECURRENCES.filter(({ frequency }) => hasSuggestedAmountsPerType[frequency]);
  const amountRecurrenceInterval = {
    onlyOneType: frequenciesWithAmounts.length === 1,
    onlyFrequency: frequenciesWithAmounts.length === 1 ? frequenciesWithAmounts[0].frequency : null,
  };

  const [hasChangedDonationAmount, setHasChangedDonationAmount] = useState(false);

  const defaultRecurrenceInterval = getDefaultRecurrenceInterval(
    hasSuggestedAmountsPerType,
    formObject.defaultAmountsFrequency ?? null
  );
  const { stripeRecurrenceInterval, setStripeRecurrenceInterval } = useContext(FrontendFormContext);

  useEffect(() => {
    setStripeRecurrenceInterval(defaultRecurrenceInterval);
  }, [defaultRecurrenceInterval, setStripeRecurrenceInterval]);

  const communicationPreferenceDefaults = useMemo(
    () => buildCommunicationPreferenceDefaults(formObject.communicationPreferences, formObject.organization?.country),
    [formObject.communicationPreferences, formObject.organization?.country]
  );

  const form = useForm<DonationPaymentInput>({
    defaultValues: {
      sepaMandateAccepted: false,
      selectedAmountId: undefined,
      recurrenceInterval: defaultRecurrenceInterval,
      answers: initAnswers(formObject.questions || []),
      ...communicationPreferenceDefaults,
      manualAmount: undefined,
      // When in preview mode and allowInHonour is enabled, show the in honor section expanded
      inHonourOf: isPreview && formObject.allowInHonour ? true : false,
      // Don't set inHonourName so the placeholder shows in preview
      inHonourName: "",
    },
    mode: validationMode,
  });

  // Prefill contact fields client-side only to prevent Cloudflare from caching another user's data in the SSR HTML.
  // Cloudflare ignores query params in the cache key, so SSR HTML must not contain user information from query params.
  useEffect(() => {
    if (isPrefillPending) {
      return;
    }
    applyContactPrefill(form, prefillSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Prefill once after hydration, when the email prefill has resolved
  }, [isPrefillPending]);

  const recurrenceInterval = useWatch({ control: form.control, name: "recurrenceInterval" });
  const suggestedAmountsToShow = suggestedAmounts.filter(
    (amount) => getStripeSubscriptionRecurrence(amount.recurrenceInterval) === recurrenceInterval
  );

  // For the live editor, we need to set the recurrence interval to the stripe recurrence interval
  useEffect(() => {
    if (isPreview) {
      form.setValue("recurrenceInterval", stripeRecurrenceInterval || null);
    }
  }, [form, stripeRecurrenceInterval, isPreview]);

  // For the live editor, update in honor section when allowInHonour setting changes
  useEffect(() => {
    if (isPreview) {
      form.setValue("inHonourOf", formObject.allowInHonour ?? false);
      // Don't set inHonourName or tribute so the placeholders show
      form.setValue("inHonourName", "");
      form.setValue("tribute", null);
    }
  }, [form, formObject.allowInHonour, isPreview]);

  const resetForm = useCallback(() => {
    // reset() replaces every value, so the preselected default has to be restated here too.
    form.reset({ ...communicationPreferenceDefaults, sepaMandateAccepted: false });
  }, [form, communicationPreferenceDefaults]);

  const description = localeField?.sanitizedDescription || localeField?.description || undefined;

  // Peer-to-peer pages ask the campaign's communication preferences question, under its title.
  const isPeerToPeerChild = Boolean(formObject.campaignId) && formObject.campaignId !== formObject.id;
  const communicationPreferencesField = isPeerToPeerChild
    ? getLocaleField(formObject.campaign?.donationFormFields)
    : localeField;

  const hasBanner = Boolean(formObject.bannerUrl || formObject.bannerVideoUrl);

  const formOrganization = formObject.organization!;

  const amount = useWatch({
    control: form.control,
    name: "amount",
  });

  const contextValue: DonationFormV2Context = {
    type: FormType.DonationForm,
    featureFlags: {},
    formObject,
    form,
    currentStep,
    setCurrentStep,
    setValidationMode,
    resetForm,
    hasBanner,
    formFields,
    description,
    localeField,
    isP2PLinkedEvent: false,
    isMembership: false, // Donation form can not be a membership
    isAuction: false, // Donation form can not be an auction
    formCategory: formObject.category,
    suggestedAmounts,
    hasSuggestedAmountsPerType,
    amountRecurrenceInterval,
    questions: formObject.questions || [],
    communicationPreferences: formObject.communicationPreferences || [],
    communicationPreferencesTitle: communicationPreferencesField?.communicationPreferencesTitle ?? null,
    formOrganization: formOrganization as OrganizationOutput,
    formCountry: formOrganization.country,
    formCurrency: getCurrencyFromCountry(formOrganization?.country),
    displayGenerateReceipt: Boolean(formOrganization.canGenerateReceipt) && formObject.hasReceipt,
    eligibleAmountTotal: amount,
    ticketsPurchased: undefined,
    suggestedAmountsToShow,
    hasChangedDonationAmount,
    setHasChangedDonationAmount,
    preselectThirdAmount: formObject.preselectThirdAmount ?? true,
    suggestedAmountsExperimentData,
    lastInteractionWasClickedAmount,
    lastClickedAmountWasIndex,
    handleSuggestedAmountClicked,
    handleManualAmountInputChanged,
    bannerAspectRatio,
    publishedOrgPageUrl: formObject.publishedOrgPageUrl ?? null,
    isEmbed,
    isPreview,
  };

  return (
    <FormProvider {...form}>
      <FormV2Context.Provider value={contextValue}>
        {!isPreview && <DafPaymentResume />}
        {/*  Only call useDonationFormUpdated if the form is not in preview mode to optimize performance of Live Editor*/}
        {!isPreview && <DonationFormUpdatedHook control={form.control} />}

        {children}
      </FormV2Context.Provider>
    </FormProvider>
  );
};

const initAnswers = (questions: QuestionOutput[]) => {
  return questions.map((question) => ({
    questionId: question.id,
    id: v4(),
    answers: [],
  }));
};

const DonationFormUpdatedHook: React.FC<{ control: Control<DonationPaymentInput> }> = ({ control }) => {
  useDonationFormUpdated({ control: control as unknown as Control<DonationFormPaymentInput> });
  return null;
};
